<template>
    <div :class="CSSClasses">
        <div class="content-col">
            <Eyebrow v-if="eyebrow" :has-underline="true">
                {{ eyebrow }}
            </Eyebrow>

            <div class="date-time" v-if="SessionDate">
                {{ SessionDate }} <span class="time">{{ SessionTimeRange }} {{ props.eventDate.timeZone }}</span>
            </div>

            <Typography v-if="eventTitle" as="h2" variant="h2" class="title">
                {{ eventTitle }}
            </Typography>

            <RichTextElements v-if="subheader" :elements="subheader" body-style="serif-large" class="subheader" />
            <RichTextElements v-if="description" :elements="description" class="description" />

            <div v-if="buttons" class="controls">
                <Button
                    v-for="(button, index) in displayButtons"
                    :url="button.url"
                    :variant="button.appearance"
                    class="cta"
                    :key="index"
                >
                    {{ button.label }}
                </Button>
            </div>
        </div>

        <div v-if="hasDetailContent" class="detail-col">
            <template v-if="peopleGroups">
                <div class="people-group" v-for="(group, index) in peopleGroups" :key="index">
                    <Eyebrow v-if="group.groupTitle" class="group-title">
                        {{ group.groupTitle }}
                    </Eyebrow>
                    <PersonBlock
                        v-for="(person, index) in group.contributors"
                        :person="person"
                        :key="index"
                        :hideDescription="true"
                        class="group-member"
                    />
                </div>
            </template>
            <VideoBlock v-if="video" v-bind="video" />
        </div>
    </div>
</template>

<script setup>
import { inject } from 'vue';

const props = defineProps({
    buttons: {
        type: Object,
    },
    description: {
        type: Object,
    },
    eventDate: {
        type: Object,
    },
    eventTitle: {
        type: String,
        default: '',
    },
    eyebrow: {
        type: String,
        default: '',
    },
    peopleGroups: {
        type: Object,
    },
    size: {
        ...sizes,
    },
    subheader: {
        type: Object,
    },
    video: {
        type: Object,
    },
});

const CSSClasses = computed(() => [
    'event-panel-block',
    `size-${props.size}`,
    `theme-${inject('tierTheme', 'light')}`,
    hasDetailContent && 'double',
]);

const hasDetailContent = computed(() => {
    return props.peopleGroups.length || props.video;
});

const displayButtons = computed(() => {
    return props.buttons?.map((item) => {
        return {
            ...item.button,
            appearance: item.button.appearance.replaceAll('Button', ''),
        };
    });
});

const SessionDate = computed(() => {
    return formatDate(props.eventDate.start, 'eee. MMMM do, yyyy');
});

const SessionTimeRange = computed(() => {
    return timeRange(props.eventDate.start, props.eventDate.end);
});
</script>

<style lang="scss" scoped>
.event-panel-block {
    @include media-query(not-phone) {
        @include grid;
    }

    .content-col,
    .detail-col {
        @include padding-lr(var(--col-gutter));
    }

    &.double.size-100 {
        .content-col {
            grid-column: 1 / span 6;
        }

        .detail-col {
            grid-column: 7 / span 6;
        }
    }

    &.size-50 {
        display: block;
        .content-col,
        .detail-col {
            grid-column: 4 / span 6;
        }
    }

    &:not(.double) {
        .content-col {
            grid-column: 4 / span 6;
        }
    }

    .date-time {
        margin-bottom: vertical-space(1);
        .time {
            color: color(noir, default, 0.4);
            margin-inline-start: 1.2rem;
        }
    }

    .controls {
        display: inline-flex;
        gap: 0.8rem;
        @include margin-tb(vertical-space(1));
    }

    .people-group {
        margin-bottom: vertical-space(1.5);
    }
    .group-title {
        @include padding-lr(var(--col-gutter));
    }

    .group-member {
        background-color: rgb(var(--color-rgb-grey-light));
        margin-bottom: 0.8rem;
    }

    :deep(.avatar img.desaturate) {
        filter: grayscale(0);
    }
    :deep(.size-50.person-block[data-v-f5afc81e]::before) {
        background-color: #ffffff;
    }

    &.theme-dark {
        .title {
            color: color(blanc);
        }

        .eyebrow,
        .subheader {
            color: color(blanc, default, 0.6);
        }

        .description {
            color: color(blanc, default, 0.9);
        }

        .date-time {
            color: color(blanc, default, 0.9);
            .time {
                color: color(blanc, default, 0.6);
            }
        }
    }
}
</style>
